/**
 * Full-profile tab rows — ATabs (5 analytics) + FTabs (6 primary).
 *
 * ONE shared open-row renderer (TabRow) drives both: top+bottom full-width
 * hairlines, INSET absolute 1px dividers between cells (not border-left),
 * gold-TEXT active (no fill/box), equal width. Matches the modal's TabNavigator
 * treatment and the package ATabs/FTabs mockups (9px/9px·0 analytics, 8.5px/10px·0
 * primary). Themeable via var(--team-primary).
 */
import React from 'react';
import { dynastyTheme } from '../../../../../../services/colorService';
import PropTypes from 'prop-types';

const C = dynastyTheme.tokens.colors;
const GOLD = C.primary;
const OFF = C.mutedLight;
const HAIRLINE = `1px solid ${C.border}`;
const DIVIDER = C.border;

const TabRow = ({ tabs, active, onChange, padY, fontSize }) => (
  <div style={{ display: 'flex', borderTop: HAIRLINE, borderBottom: HAIRLINE }}>
    {tabs.map((t, i) => {
      const isActive = active === t.key;
      return (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          role="tab"
          aria-selected={isActive}
          data-active={isActive ? 'true' : 'false'}
          style={{
            flex: 1, position: 'relative', textAlign: 'center', padding: `${padY} 0`,
            border: 'none', background: 'transparent', cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {i > 0 && (
            <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 6, bottom: 6, width: 1, background: DIVIDER }} />
          )}
          <span style={{
            fontSize, fontWeight: 800, textTransform: 'none', whiteSpace: 'nowrap',
            color: isActive ? GOLD : OFF, userSelect: 'none',
          }}>{t.label}</span>
        </button>
      );
    })}
  </div>
);

TabRow.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })).isRequired,
  active: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  padY: PropTypes.string,
  fontSize: PropTypes.string,
};

export const ATAB_KEYS = ['sum', 'heat', 'rank', 'imp', 'trends'];
const A_TABS = [
  { key: 'sum', label: 'Summary' },
  { key: 'heat', label: 'Heatmap' },
  { key: 'rank', label: 'Rankings' },
  { key: 'imp', label: 'Impact' },
  { key: 'trends', label: 'Trends' },
];

// Order matches the modal's TabNavigator exactly (Info first), so the two tab
// bars can never diverge.
export const FTAB_KEYS = ['info', 'stats', 'logs', 'scouting', 'news', 'fin'];
const F_TABS = [
  { key: 'info', label: 'Info' },
  { key: 'stats', label: 'Stats' },
  { key: 'logs', label: 'Game Logs' },
  { key: 'scouting', label: 'Scouting' },
  { key: 'news', label: 'News' },
  { key: 'fin', label: 'Financials' },
];

export const ATabs = ({ active, onChange }) => (
  <TabRow tabs={A_TABS} active={active} onChange={onChange} padY="9px" fontSize="9px" />
);
ATabs.propTypes = { active: PropTypes.string, onChange: PropTypes.func.isRequired };

export const FTabs = ({ active, onChange }) => (
  <TabRow tabs={F_TABS} active={active} onChange={onChange} padY="10px" fontSize="8.5px" />
);
FTabs.propTypes = { active: PropTypes.string, onChange: PropTypes.func.isRequired };
